import Draggable from 'draggable';

import bem from './bem';
import utils from './utils';
import viewport from './viewport';

class Scrollbar {

	static get block() {
		return bem.block('scrollbar');
	}

	get id() {
		return this._id;
	}

	static get counter() {
		Scrollbar._counter = (Scrollbar._counter || 0) + 1;
		return Scrollbar._counter;
	}

	constructor($scrollbar){

		this._id = Scrollbar.counter;

		this.$scrollbar = $scrollbar;
		this.$contentWrapper = $scrollbar.querySelector(Scrollbar.block.selector('content-wrapper'));
		this.$content = $scrollbar.querySelector(Scrollbar.block.selector('content'));
		this.$track = {
			root: null,
			rail: null,
			handle: null
		};

		this.settings = {
			scrollable: null,
			rootWidth: null,
			contentWidth: null,
			track: {
				root: null,
				rail: null,
				handle: null,
				maxHandle: null
			}
		};
		this.scroll = {
			max: null,
			position: 0,
			scrolled: false,
			atEnd: false
		};

		this.initialised = null;
		this.draggable = null;

		this.start = { x: 0, y: 0 };

		this.init = this.init.bind(this);
		this.onResize = this.onResize.bind(this);
		this.onScroll = this.onScroll.bind(this);
		this.onMousewheel = this.onMousewheel.bind(this);
		this.onTouchmove = this.onTouchmove.bind(this);
		this.create = this.create.bind(this);
		this.destroy = this.destroy.bind(this);
		this.updateInterface = this.updateInterface.bind(this);
		this.toggleScrollbar = this.toggleScrollbar.bind(this);
		this.scrollContent = this.scrollContent.bind(this);
		this.scrollHandle = this.scrollHandle.bind(this);

		this.init();

	}

	init(){

		// add resize event to reset and check if overflow is required
		viewport.addListener('scrollbar-' + this.id, this.onResize);
		this.onResize();

	}

	onResize(){

		this.settings.rootWidth = this.$contentWrapper.offsetWidth;
		this.settings.contentWidth = this.$content.offsetWidth;
		this.settings.scrollable = this.settings.contentWidth > this.settings.rootWidth;
		this.scroll.max = this.settings.contentWidth - this.settings.rootWidth;

		this.toggleScrollbar(this.settings.scrollable);

		this.$scrollbar.classList.toggle('is-scrollable', this.settings.scrollable);

	}

	onScroll(){

		const $scrollbar = this.$scrollbar;
		const $contentWrapper = this.$contentWrapper;

		const scroll = this.scroll;

		scroll.position = $contentWrapper.scrollLeft;

		// detect if scroll has occured to add left fade
		if( scroll.position > 0 && !scroll.scrolled ){

			scroll.scrolled = true;

			$scrollbar.classList.add('is-scrolled');

		}else if( scroll.position <= 0 && scroll.scrolled ){

			scroll.scrolled = false;

			$scrollbar.classList.remove('is-scrolled');

		}

		// detect if at end of scroll, to remove right fade
		if( scroll.position >= scroll.max - 10 && !scroll.atEnd ){

			scroll.atEnd = true;

			$scrollbar.classList.add('is-scroll-end');

		}else if( scroll.position < scroll.max - 10 && scroll.atEnd ){

			scroll.atEnd = false;

			$scrollbar.classList.remove('is-scroll-end');

		}

	}

	onMousewheel(e){

		const delta = e.deltaX;

		this.$contentWrapper.scrollLeft += delta;

		this.scrollHandle();

		if( delta !== 0 )
			e.preventDefault();

	}

	onTouchmove(){

		this.scrollHandle();

	}

	create(){

		const $contentWrapper = this.$contentWrapper;

		const scrollContent = this.scrollContent;

		// create scrollbar markup
		this.$track.handle = document.createElement('div');
		this.$track.handle.classList.add(Scrollbar.block.class('track-handle'), 'scrollbar__track-handle');

		this.$track.rail = document.createElement('div');
		this.$track.rail.classList.add(Scrollbar.block.class('track-rail'), 'scrollbar__track-rail');
		this.$track.rail.appendChild(this.$track.handle);

		this.$track.root = document.createElement('div');
		this.$track.root.classList.add(Scrollbar.block.class('track'), 'scrollbar__track');
		this.$track.root.appendChild(this.$track.rail);

		this.$scrollbar.appendChild(this.$track.root);

		// set state
		this.initialised = true;

		// create draggable
		this.draggable = new Draggable(this.$track.handle, {
			onDrag: function(element, x){
				scrollContent(x);
			}
		});

		// update interface sizes
		this.updateInterface();

		// add scroll listener
		$contentWrapper.addEventListener('scroll', this.onScroll);

		// add mousewheel listener
		$contentWrapper.addEventListener('wheel', this.onMousewheel);

		// touch move listener
		$contentWrapper.addEventListener('touchmove', this.onTouchmove);

	}

	destroy(){

		const $contentWrapper = this.$contentWrapper;

		// set state
		this.initialised = false;

		// remove draggable
		this.draggable.destroy();

		// remove scroll listener
		$contentWrapper.removeEventListener('scroll', this.onScroll);

		// remove mousewheel listener
		$contentWrapper.removeEventListener('wheel', this.onMousewheel);

		// remove touch move listener
		$contentWrapper.removeEventListener('touchmove', this.onMousewheel);

		// remove scrollbar markup
		this.$track.root.parentNode.removeChild(this.$track.root);
		this.$track.root = null;
		this.$track.rail = null;
		this.$track.handle = null;

	}

	updateInterface(){

		const scrollMetrics = utils.getMaxScroll(this.$contentWrapper);
		const handleWidth = (100 / scrollMetrics.innerX) * this.$contentWrapper.offsetWidth;

		this.settings.track.rail = this.$track.rail.offsetWidth;

		this.$track.handle.style.width = (handleWidth / 1.5) + '%';

		this.settings.track.handle = this.$track.handle.offsetWidth;

		this.settings.track.maxHandle = this.settings.track.rail - this.settings.track.handle;

		// set draggable limits
		this.draggable.setOption('limit', { x: [0, this.settings.track.maxHandle], y: false });

	}

	toggleScrollbar(force){

		if( !this.initialised && force ){

			this.create();

			this.initialised = true;

		}else if( this.initialised && !force ){

			this.destroy();

		}else if( this.initialised ){

			this.updateInterface();

		}

	}

	scrollContent(x){

		const settings = this.settings;
		const scroll = this.scroll;

		const left = utils.rangeToRange(x, settings.track.maxHandle, 0, scroll.max, 0);

		this.$contentWrapper.scrollLeft = left;

	}

	scrollHandle(){

		const settings = this.settings;
		const scroll = this.scroll;

		const left = utils.rangeToRange(scroll.position, scroll.max, 0, settings.track.maxHandle, 0);

		this.$track.handle.style.left = left + 'px';

	}

}

module.exports = Scrollbar;

// module.exports = () => {

// 	[].slice
// 		.call(document.querySelectorAll(Scrollbar.block.selector()))
// 		.forEach($scrollbar => new Scrollbar($scrollbar));

// };



// WEBPACK FOOTER //
// ./src/js/modules/scrollbar.js